import { useEffect, useRef, type RefObject } from 'react'
import { useMotion } from '@/components/motion/motion'
import { createDemo, supportsDemos, type DemoControls, type DemoImpl } from '@/components/motion/demos/demoKit'

/**
 * Builds a motion illustration inside an empty stage element and returns its controls. The stage
 * stays empty where the Web Animations API is unavailable. Controls are ready in effects declared
 * after this hook.
 */
export const useDemo = <Context>(stageRef: RefObject<HTMLElement | null>, impl: DemoImpl<Context>) => {
  const { paused, reduceMotion } = useMotion()
  const motion = useRef({ paused, reduceMotion })
  const controls = useRef<DemoControls | null>(null)

  useEffect(() => {
    motion.current = { paused, reduceMotion }
  }, [paused, reduceMotion])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage || !supportsDemos()) return
    const demo = createDemo(stage, impl, () => !motion.current.paused && !motion.current.reduceMotion)
    controls.current = demo
    return () => {
      demo.dispose()
      controls.current = null
    }
  }, [stageRef, impl])

  return controls
}
